/**
 * Страховка от белого экрана.
 *
 * ПОЧЕМУ ПОЯВИЛСЯ (Антон, 2026-09-10 12:57): исключение в теле рендера
 * (`throw new Error('Тред не найден')`) уронило ВСЁ приложение — React
 * размонтировал дерево целиком, остался пустой белый документ, из которого
 * нельзя выйти: ни списка комнат, ни кнопки «назад», ни меню. Конкретную
 * причину я починил, но любой следующий баг в рендере дал бы тот же
 * результат. Граница ошибок превращает «мёртвая вкладка» в «плашка с
 * кнопкой вернуться».
 *
 * Сознательно НЕ ловит: ошибки в обработчиках событий и в промисах —
 * React их сюда не приносит. Для них есть обычные try/catch и
 * `humanError` в местах вызова.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { tr } from "../i18n";

import { color, font, radius, space } from "./theme";

interface Props {
  children: ReactNode;
  /** Куда возвращать: сбросить адрес и состояние. */
  onReset?: () => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // В консоль — со стеком компонентов: без него по минифицированному
    // бандлу причину не найти.
    console.error("[ALG] сбой рендера:", error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        data-testid="error-boundary"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          padding: space.lg,
          background: color.bg,
        }}
      >
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <p style={{ fontWeight: 600, color: color.text }}>{tr("error.somethingBroken")}</p>
          <p style={{ fontSize: font.small, color: color.textDim }}>{tr("error.renderStopped")}</p>
          <pre
            style={{
              fontSize: font.tiny,
              color: color.textDim,
              background: color.sidebarBg,
              border: `1px solid ${color.border}`,
              borderRadius: radius.sm,
              padding: space.sm,
              textAlign: "left",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 120,
              overflow: "auto",
            }}
          >
            {error.message}
          </pre>
          <button
            type="button"
            onClick={this.reset}
            data-testid="error-reset"
            style={{
              marginTop: space.md,
              padding: "8px 16px",
              font: "inherit",
              fontSize: font.small,
              color: color.textOnAccent,
              background: color.accent,
              border: "none",
              borderRadius: radius.sm,
              cursor: "pointer",
            }}
          >{tr("error.backToChats")}</button>
        </div>
      </div>
    );
  }
}
